import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { startRun } from "@/api/runs";
import type { StartInputs } from "@/api/create-types";
import { generationErrorMessage } from "@/api/llm-access";
import { Alert, Button } from "@/components/ui";

const LANGUAGE_TRAINER = "language-trainer";

type Intent = "balanced" | "review" | "reinforce" | "advance";

const INTENTS: { value: Intent; label: string; hint: string }[] = [
  { value: "balanced", label: "Balanced", hint: "Eddi adapts automatically" },
  { value: "review", label: "Review", hint: "Drill what's due + what I missed" },
  { value: "reinforce", label: "Reinforce", hint: "Go deeper on what I just did" },
  { value: "advance", label: "Advance", hint: "New topics, skip what I've mastered" },
];

/** Steer + start the next language-trainer set. `baseInputs` carries either
 *  `{ continue_from_set_id }` (post-session — chains from the set just finished)
 *  or `{ target_language, level }` (the Continue tile). The learner picks an
 *  intent + optional free-text focus; the backend turns those into the planner's
 *  continuation steer on top of the adaptive mastery/SRS signals. */
export function ContinueChooser({
  baseInputs,
  onStarted,
}: {
  baseInputs: StartInputs;
  onStarted: () => void;
}) {
  const { t } = useTranslation();
  const [intent, setIntent] = useState<Intent>("balanced");
  const [focus, setFocus] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      startRun(LANGUAGE_TRAINER, {
        ...baseInputs,
        continuation_intent: intent,
        ...(focus.trim() ? { next_focus: focus.trim() } : {}),
      }),
    onSuccess: onStarted,
    onError: (e) => setError(generationErrorMessage(e, t)),
  });

  return (
    <div className="space-y-3 text-left">
      {error && <Alert>{error}</Alert>}
      <div className="grid grid-cols-2 gap-2">
        {INTENTS.map((opt) => {
          const on = intent === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setIntent(opt.value)}
              className={`rounded-xl border px-3 py-2 text-left transition ${
                on
                  ? "border-brand-500 bg-brand-50"
                  : "border-black/10 bg-white hover:bg-brand-50/50"
              }`}
            >
              <span className="block text-sm font-semibold text-ink">{t(opt.label)}</span>
              <span className="block text-xs text-ink-faint">{t(opt.hint)}</span>
            </button>
          );
        })}
      </div>
      <input
        value={focus}
        onChange={(e) => setFocus(e.target.value)}
        placeholder={t("Focus next on… (optional)")}
        className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
      />
      <Button
        size="lg"
        className="w-full"
        loading={mut.isPending}
        onClick={() => {
          setError(null);
          mut.mutate();
        }}
      >
        {t("Continue learning")}
      </Button>
    </div>
  );
}
